import React, { useEffect, useState } from 'react';
import { getWorker, updateBookingAdvanceDays, updateBreakBetweenAppointments } from './api';
import { Worker, Location } from './types';
import LocationManager from './LocationManager';
import CustomSlugForm from './profile/CustomSlugForm';
import DurationSettings from './profile/DurationSettings';
import GoogleCalendarSection from './profile/GoogleCalendarSection';
import IcsSection from './profile/IcsSection';
import BioForm from './profile/BioForm';
import LogoUploadForm from './profile/LogoUploadForm';
import MfaSettings from './profile/MfaSettings';
import NumericFieldForm from './profile/NumericFieldForm';
import PasswordChangeForm from './profile/PasswordChangeForm';
import ProfileForm from './profile/ProfileForm';
import { useLang } from './LangContext';
import './ProfileManager.css';

interface Props {
  token: string;
  onDurationOptionsChange?: (opts: number[]) => void;
  onDefaultDurationChange?: (value: number | null) => void;
  onCustomPublicIdChange?: (slug: string | null) => void;
  onLocationsChange?: (locations: Location[]) => void;
  onLogoChange?: (url: string | null) => void;
}

// Top-level container that loads the current worker and composes the per-section
// forms. Each subcomponent owns its own state so they can save independently.
const ProfileManager = ({
  token,
  onDurationOptionsChange,
  onDefaultDurationChange,
  onCustomPublicIdChange,
  onLocationsChange,
  onLogoChange,
}: Props) => {
  const { t } = useLang();
  const [worker, setWorker] = useState<Worker | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await getWorker(token);
        if (!cancelled) setWorker(data);
      } catch (err) {
        console.error('Error loading profile:', err);
        if (!cancelled) setLoadError('Failed to load profile data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading || !worker) {
    return (
      <div className="profile-manager">
        {loadError ? (
          <div className="error-message"><strong>{t.errorLabel2}</strong> {loadError}</div>
        ) : (
          <div className="profile-loading">
            <div className="loading-text">{t.loadingProfile}</div>
          </div>
        )}
      </div>
    );
  }

  const initialAdvDays = typeof worker.booking_advance_days === 'number' ? worker.booking_advance_days : 90;
  const initialBreak = typeof worker.break_between_appointments === 'number' ? worker.break_between_appointments : 0;

  return (
    <div className="profile-manager">
      <LogoUploadForm
        token={token}
        initialLogoUrl={worker.logo_url}
        onChange={onLogoChange}
      />

      <BioForm token={token} initialBio={worker.bio} />

      <ProfileForm
        token={token}
        initialFirstName={worker.first_name}
        initialLastName={worker.last_name}
        initialEmail={worker.e_mail}
        initialPendingEmail={worker.pending_email}
      />

      <LocationManager token={token} onLocationsChange={onLocationsChange} />

      <DurationSettings
        token={token}
        initialOptions={worker.duration_options}
        initialDefault={worker.default_duration}
        onOptionsChange={onDurationOptionsChange}
        onDefaultChange={onDefaultDurationChange}
      />

      <CustomSlugForm
        token={token}
        initialSlug={worker.custom_public_id}
        onChange={onCustomPublicIdChange}
      />

      <NumericFieldForm
        title={t.bookingHorizonTitle}
        hint={t.bookingHorizonHint}
        min={7}
        max={365}
        initialValue={initialAdvDays}
        successMessage={t.bookingHorizonSuccess}
        errorMessage={t.bookingHorizonError}
        validate={(n) => (Number.isNaN(n) || n < 7 || n > 365 ? t.bookingHorizonValidate : null)}
        save={async (n) => {
          const res = await updateBookingAdvanceDays(n, token);
          return res.booking_advance_days;
        }}
      />

      <NumericFieldForm
        title={t.breakTimeTitle}
        hint={t.breakTimeHint}
        min={0}
        max={120}
        step={5}
        initialValue={initialBreak}
        successMessage={t.breakTimeSuccess}
        errorMessage={t.breakTimeError}
        validate={(n) => (Number.isNaN(n) || n < 0 || n > 120 ? t.breakTimeValidate : null)}
        save={async (n) => {
          const res = await updateBreakBetweenAppointments(n, token);
          return res.break_between_appointments;
        }}
      />

      <PasswordChangeForm token={token} />

      <IcsSection token={token} />

      <GoogleCalendarSection token={token} />

      <MfaSettings token={token} />
    </div>
  );
};

export default ProfileManager;
